import { Tooltip } from '@/ui'
import { attachClosestEdge, type Edge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import ctl from '@netlify/classnames-template-literals'
import { LockIcon, Trash2Icon } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CallIcon, HiredIcon, InterviewIcon, NewIcon, OfferIcon, RejectedIcon } from '../../../../../../constructs'
import { SelectionCheckbox } from '../../../selection-checkbox'
import { DRAG_TYPES } from '../constants'
import { CountBadge } from './count-badge'

type StageName = 'new' | 'call' | 'interview' | 'offer' | 'hired' | 'rejected'

const stageHeaderStyles: Record<
	StageName,
	{ bgClass: string; textClass: string; badgeClass: string; shadowClass: string; dropClass: string }
> = {
	new: {
		bgClass: 'bg-gradient-to-b from-lime-50 to-lime-100',
		textClass: 'text-lime-800',
		badgeClass: 'bg-lime-800/20 text-lime-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-lime-300)]',
		dropClass: 'bg-lime-50',
	},
	call: {
		bgClass: 'bg-gradient-to-b from-cyan-50 to-cyan-100',
		textClass: 'text-cyan-800',
		badgeClass: 'bg-cyan-800/20 text-cyan-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-cyan-300)]',
		dropClass: 'bg-cyan-50',
	},
	interview: {
		bgClass: 'bg-gradient-to-b from-purple-50 to-purple-100',
		textClass: 'text-purple-800',
		badgeClass: 'bg-purple-800/20 text-purple-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-purple-300)]',
		dropClass: 'bg-purple-50',
	},
	offer: {
		bgClass: 'bg-gradient-to-b from-amber-50 to-amber-100',
		textClass: 'text-amber-800',
		badgeClass: 'bg-amber-800/20 text-amber-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-amber-300)]',
		dropClass: 'bg-amber-50',
	},
	hired: {
		bgClass: 'bg-gradient-to-b from-green-50 to-green-100',
		textClass: 'text-green-800',
		badgeClass: 'bg-green-800/20 text-green-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-green-300)]',
		dropClass: 'bg-green-50',
	},
	rejected: {
		bgClass: 'bg-gradient-to-b from-red-50 to-red-100',
		textClass: 'text-red-800',
		badgeClass: 'bg-red-800/20 text-red-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-red-300)]',
		dropClass: 'bg-red-50',
	},
}

const stageIcons: Record<StageName, React.ComponentType> = {
	new: NewIcon,
	call: CallIcon,
	interview: InterviewIcon,
	offer: OfferIcon,
	hired: HiredIcon,
	rejected: RejectedIcon,
}

function getStageStyle(
	name: string,
): {
	bgClass: string
	textClass: string
	badgeClass: string
	shadowClass: string
	dropClass: string
	Icon: React.ComponentType | null
} {
	const normalizedName = name.toLowerCase() as StageName
	const style = stageHeaderStyles[normalizedName]
	const Icon = stageIcons[normalizedName] ?? null

	if (style) {
		return { ...style, Icon }
	}

	return {
		bgClass: 'bg-gradient-to-b from-gray-50 to-gray-100',
		textClass: 'text-gray-600',
		badgeClass: 'bg-gray-800/20 text-gray-900',
		shadowClass: 'shadow-[0_2px_0_var(--color-gray-300)]',
		dropClass: 'bg-gray-50',
		Icon: null,
	}
}

export type ColumnProps = {
	id: string
	name: string
	count: number
	children: React.ReactNode
	columnReorderMode?: boolean
	isDraggable?: boolean
	isDraggingColumn?: boolean
	editMode?: boolean
	locked?: boolean
	onNameChange?: (newName: string) => void
	onDelete?: () => void
	autoFocusName?: boolean
	onSelectAll?: () => void
	selectAllChecked?: boolean
	selectAllIndeterminate?: boolean
}

export function Column({
	id,
	name,
	count,
	children,
	columnReorderMode = false,
	isDraggable = false,
	isDraggingColumn = false,
	editMode = false,
	locked = false,
	onNameChange,
	onDelete,
	autoFocusName = false,
	onSelectAll,
	selectAllChecked = false,
	selectAllIndeterminate = false,
}: ColumnProps) {
	const { t } = useTranslation('common')
	const columnRef = useRef<HTMLDivElement>(null)
	const headerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const [isOver, setIsOver] = useState(false)
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null)
	const [isEditing, setIsEditing] = useState(autoFocusName && editMode)
	const [editedName, setEditedName] = useState(name)

	/**
	 * Setup drop target for candidates
	 * Only active when NOT in column reorder mode
	 */
	useEffect(() => {
		const element = columnRef.current
		if (!element || columnReorderMode) { return }

		return dropTargetForElements({
			element,
			getData: () => ({ stageId: id }),
			onDragEnter: () => setIsOver(true),
			onDragLeave: () => setIsOver(false),
			onDrop: () => setIsOver(false),
		})
	}, [id, columnReorderMode])

	/**
	 * Setup drop target for column reordering
	 * Only active when IN column reorder mode
	 * Uses edge detection to determine where to insert the dragged column
	 */
	useEffect(() => {
		const element = columnRef.current
		if (!element || !columnReorderMode) { return }

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => source.data.type === DRAG_TYPES.COLUMN,
			getData: ({ input, element }) => {
				const data = { type: DRAG_TYPES.COLUMN, columnId: id }
				return attachClosestEdge(data, {
					input,
					element,
					allowedEdges: ['left', 'right'],
				})
			},
			onDragEnter: ({ self }) => {
				const edge = extractClosestEdge(self.data)
				setClosestEdge(edge)
			},
			onDrag: ({ self }) => {
				const edge = extractClosestEdge(self.data)
				setClosestEdge(edge)
			},
			onDragLeave: () => setClosestEdge(null),
			onDrop: () => setClosestEdge(null),
		})
	}, [id, columnReorderMode])

	/**
	 * Setup draggable for column header
	 * Only active when column is draggable and in column reorder mode
	 * The header is the drag handle for reordering
	 */
	useEffect(() => {
		const element = headerRef.current
		if (!element || !isDraggable || !columnReorderMode) { return }

		return draggable({
			element,
			getInitialData: () => ({ type: DRAG_TYPES.COLUMN, columnId: id }),
		})
	}, [id, isDraggable, columnReorderMode])

	// Reset edited name when name prop changes
	useEffect(() => {
		setEditedName(name)
	}, [name])

	// Focus input when entering edit mode
	useEffect(() => {
		if (isEditing && inputRef.current) {
			inputRef.current.focus()
			inputRef.current.select()
		}
	}, [isEditing])

	// Sync isEditing with editMode - tear down edit UI when editMode is toggled off
	useEffect(() => {
		if (!editMode) {
			setIsEditing(false)
			setEditedName(name)
			inputRef.current?.blur()
		}
	}, [editMode, name])

	const handleNameClick = () => {
		if (editMode && !locked && onNameChange) {
			setIsEditing(true)
		}
	}

	const handleNameBlur = () => {
		setIsEditing(false)
		if (!editMode || !onNameChange) {
			setEditedName(name)
			return
		}
		const trimmed = editedName.trim()
		if (trimmed && trimmed !== name) {
			onNameChange(trimmed)
		} else {
			setEditedName(name)
		}
	}

	const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault()
			// Blur the input to trigger onBlur handler, avoiding double commit
			e.currentTarget.blur()
		} else if (e.key === 'Escape') {
			setEditedName(name)
			setIsEditing(false)
		}
	}

	const { bgClass, textClass, badgeClass, shadowClass, dropClass, Icon } = getStageStyle(name)

	return (
		<div
			ref={columnRef}
			className={ctl(`
				  flex flex-col bg-white rounded-t-lg first:rounded-bl-lg last:rounded-br-lg
				  h-full transition-all relative
				  ${isDraggingColumn ? 'opacity-50' : ''}
				`)}
			style={{ minWidth: 'var(--card-width)', width: 'var(--card-width)' }}
		>
			{/* Visual indicator for left edge drop zone */}
			{closestEdge === 'left' && <div className="absolute left-0 top-0 bottom-0 w-1 bg-purple-500 z-10" />}

			{/* Visual indicator for right edge drop zone */}
			{closestEdge === 'right' && <div className="absolute right-0 top-0 bottom-0 w-1 bg-purple-500 z-10" />}

			{/* Column header with stage name and count */}
			<div
				ref={headerRef}
				className={ctl(`
					group p-4 rounded-t-lg ${bgClass} ${shadowClass} font-semibold text-sm uppercase ${textClass}
					flex justify-between items-center transition-all relative z-[1]
					${isDraggable && columnReorderMode ? 'cursor-move hover:brightness-95' : ''}
					${!isDraggable && columnReorderMode ? 'opacity-50' : ''}
					${editMode ? 'ring-2 ring-inset ring-purple-200' : ''}
				`)}
			>
				{onSelectAll && count > 0 && !editMode && (
					<div className="mr-2 shrink-0">
						<SelectionCheckbox
							checked={selectAllChecked}
							indeterminate={selectAllIndeterminate}
							onToggle={() => onSelectAll()}
							label={`${t('bulkActions.selectAll')}: ${name}`}
							alwaysVisible
						/>
					</div>
				)}
				<div className="flex items-center gap-2 select-none flex-1 min-w-0">
					{Icon && <Icon />}
					{isEditing
						? (
							<input
								ref={inputRef}
								type="text"
								value={editedName}
								onChange={(e) => setEditedName(e.target.value)}
								onBlur={handleNameBlur}
								onKeyDown={handleNameKeyDown}
								className="bg-white/80 px-2 py-0.5 rounded text-sm font-semibold uppercase w-full min-w-0 outline-none ring-2 ring-purple-400"
							/>
						)
						: (
							<span
								onClick={handleNameClick}
								className={ctl(`
								truncate
								${editMode && !locked ? 'cursor-text hover:bg-white/30 px-2 py-0.5 -mx-2 rounded' : ''}
							`)}
							>
								{name}
							</span>
						)}
					<CountBadge count={count} className={badgeClass} />
				</div>
				{editMode && (
					<div className="flex items-center gap-1 ml-2">
						{locked
							? (
								<span
									className="p-1 opacity-60"
									aria-label={t('pipeline.lockedStage')}
									title={t('pipeline.lockedStageDescription')}
								>
									<LockIcon className="size-4" />
								</span>
							)
							: (
								<Tooltip content="Delete stage">
									<button
										onClick={onDelete}
										className="p-1 rounded hover:bg-red-100 hover:text-red-600 transition-colors"
										aria-label={t('pipeline.deleteStage')}
									>
										<Trash2Icon className="size-4" />
									</button>
								</Tooltip>
							)}
					</div>
				)}
			</div>

			{/* Scrollable content area */}
			<div className="flex-1 relative">
				<div
					className={`absolute inset-0 overflow-y-auto overflow-x-hidden py-3 px-5 flex flex-col gap-3 transition-colors ${
						isOver ? dropClass : ''
					}`}
					style={{ scrollbarWidth: 'thin' }}
				>
					{React.Children.count(children) === 0
						? (
							<div className="select-none flex items-center justify-center h-full text-gray-400 text-sm italic">
								No candidates
							</div>
						)
						: (
							children
						)}
				</div>

				{/* Overlay during column reorder mode to prevent interaction */}
				{columnReorderMode && <div className="absolute inset-0 bg-white/50 backdrop-blur" />}
			</div>
		</div>
	)
}
